import axios, { AxiosInstance, AxiosError } from 'axios'
import { message } from 'antd'
import type {
  ApiResponse,
  PaginatedResponse,
  Project,
  ProjectFilters,
  OutreachRecord,
  Reply,
  AccountState,
  DailyStats,
  DashboardStats,
  OutreachTask,
  BatchResult,
  SystemConfig,
  PreviewContent,
} from '../types'

// 创建axios实例
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 获取 impersonation ID (avoid circular import)
const getImpersonationId = (): string | null => {
  try {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      const parsed = JSON.parse(authStorage)
      return parsed?.state?.impersonatedUserId || null
    }
  } catch {
    // ignore
  }
  return null
}

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 添加 impersonation header (管理员查看其他用户)
    const impersonatedUserId = getImpersonationId()
    if (impersonatedUserId) {
      config.headers['X-Impersonate-User'] = impersonatedUserId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiResponse<unknown>>) => {
    const respData = error.response?.data as Record<string, unknown> | undefined
    const errorMessage = respData?.detail || respData?.error || error.message || '请求失败'
    message.error(errorMessage)
    return Promise.reject(error)
  }
)

// ==================== 认证相关 API ====================

// Auth API uses different base path
const authAxios = axios.create({
  baseURL: '/auth',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

authAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiResponse<unknown>>) => {
    // Don't show error message for auth failures on login
    if (error.config?.url !== '/login') {
      const respData = error.response?.data as Record<string, unknown> | undefined
      const errorMessage = respData?.detail || respData?.error || error.message || '请求失败'
      message.error(errorMessage)
    }
    return Promise.reject(error)
  }
)

export interface LoginResponse {
  access_token: string
  token_type: string
  user: {
    id: string
    email: string
    name: string
    role: 'admin' | 'bd'
    is_active: boolean
    created_at: string
    last_login_at: string | null
  }
}

export const authApi = {
  // 登录
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const { data } = await authAxios.post('/login', { email, password })
    return data
  },

  // 获取当前用户
  getMe: async () => {
    const { data } = await authAxios.get('/me')
    return data
  },

  // 修改密码
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await authAxios.post('/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
  },

  // 获取所有用户 (管理员)
  getUsers: async () => {
    const { data } = await authAxios.get('/users')
    return data
  },

  // 获取BD用户列表 (管理员)
  getBdUsers: async () => {
    const { data } = await authAxios.get('/users/bd')
    return data
  },

  // 创建用户 (管理员)
  createUser: async (userData: {
    email: string
    password: string
    name: string
    role?: 'admin' | 'bd'
  }) => {
    const { data } = await authAxios.post('/users', userData)
    return data
  },

  // 更新用户 (管理员)
  updateUser: async (userId: string, userData: {
    name?: string
    is_active?: boolean
    user_status?: 'active' | 'inactive' | 'suspended'
  }) => {
    const { data } = await authAxios.patch(`/users/${userId}`, userData)
    return data
  },

  // 删除用户 (管理员)
  deleteUser: async (userId: string): Promise<void> => {
    await authAxios.delete(`/users/${userId}`)
  },

  // 重置用户密码 (管理员)
  resetUserPassword: async (userId: string): Promise<{ new_password: string }> => {
    const { data } = await authAxios.post(`/users/${userId}/reset-password`)
    return data
  },

  // BD 离职处理（管理员）
  departUser: async (userId: string, payload: {
    handover_to_user_id?: string
    departure_note?: string
  }): Promise<{
    status: string
    user_id: string
    user_status: string
    departed_at: string
    projects_transferred: number
    projects_conflict_flagged: number
  }> => {
    const { data } = await authAxios.post(`/users/${userId}/depart`, payload)
    return data
  },
}

// ==================== 项目相关 API ====================

export const projectApi = {
  // 获取项目列表
  getList: async (filters?: ProjectFilters): Promise<PaginatedResponse<Project>> => {
    const { data } = await api.get('/projects', { params: filters })
    return data
  },

  // 获取单个项目
  getById: async (id: string): Promise<Project> => {
    const { data } = await api.get(`/projects/${id}`)
    return data
  },

  // 创建项目
  create: async (project: Partial<Project>): Promise<Project> => {
    const { data } = await api.post('/projects', project)
    return data
  },

  // 更新项目
  update: async (id: string, project: Partial<Project>): Promise<Project> => {
    const { data } = await api.put(`/projects/${id}`, project)
    return data
  },

  // 删除项目
  delete: async (id: string): Promise<void> => {
    await api.delete(`/projects/${id}`)
  },

  // 下载导入模板
  downloadImportTemplate: async (fmt: 'xlsx' | 'csv' = 'xlsx'): Promise<void> => {
    const { data } = await api.get('/projects/import/template', { params: { fmt }, responseType: 'blob' })
    const ext = fmt === 'csv' ? 'csv' : 'xlsx'
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `import_template.${ext}`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },

  // 批量导入项目
  importProjects: async (file: File): Promise<BatchResult> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await api.post('/projects/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },

  // 导出项目
  exportProjects: async (filters?: ProjectFilters): Promise<Blob> => {
    const { data } = await api.get('/projects/export', {
      params: filters,
      responseType: 'blob',
    })
    return data
  },

  // 重新评分
  rescore: async (id: string): Promise<Project> => {
    const { data } = await api.post(`/projects/${id}/rescore`)
    return data
  },

  // 批量删除
  batchDelete: async (params: string[] | {
    select_all?: boolean; ids?: string[]
    filters?: { status?: string; search?: string; scrape_filter?: string }
  }): Promise<BatchResult> => {
    const body = Array.isArray(params) ? { ids: params } : params
    const { data } = await api.post('/projects/batch-delete', body)
    return data
  },

  // 批量评分
  batchRescore: async (ids: string[]): Promise<BatchResult> => {
    const { data } = await api.post('/projects/batch-rescore', { ids })
    return data
  },

  // 移入冷池
  moveToColdPool: async (id: string): Promise<Project> => {
    const { data } = await api.post(`/projects/${id}/move-to-cold-pool`)
    return data
  },

  // 从冷池恢复
  restoreFromColdPool: async (id: string): Promise<Project> => {
    const { data } = await api.post(`/projects/${id}/reactivate`)
    return data
  },

  // GBR 实时校验（新建/编辑项目时 onBlur）
  gbrCheck: async (twitterHandle: string): Promise<{
    allowed: boolean
    skipped?: boolean
    reason?: string
    crm_bd?: string
    crm_lead_id?: string
  }> => {
    const { data } = await api.get('/v1/projects/gbr-check', {
      params: { twitter_handle: twitterHandle },
    })
    return data
  },

  // 从 CRM 主动拉取归属（BD 触发，仅更新冲突状态）
  syncFromCrm: async (): Promise<{
    status: string
    total: number
    synced: number
    conflicts_found: number
    errors: number
    message?: string
  }> => {
    const { data } = await api.post('/v1/projects/sync-from-crm')
    return data
  },

  // 从 CRM 导入项目（BD 触发，创建 BB 中不存在的 CRM 项目）
  importFromCrm: async (): Promise<{
    status: string
    message?: string
    total_from_crm: number
    created: number
    updated: number
    skipped_conflict: number
    skipped_no_handle: number
    errors: number
    conflict_items: Array<{ twitter_handle: string; bb_owner: string; crm_assigned: string }>
    created_items: Array<{ twitter_handle: string; bb_project_id: string; project_name: string }>
  }> => {
    const { data } = await api.post('/v1/projects/import-from-crm')
    return data
  },

  // 触发冲突检测（单个或批量）
  checkConflict: async (projectIds: string[]): Promise<{
    status: string
    message?: string
    results: Array<{
      project_id: string
      project_name?: string
      twitter_handle?: string
      result: Record<string, unknown>
      error?: string
    }>
  }> => {
    const { data } = await api.post('/v1/projects/conflict-check', { project_ids: projectIds })
    return data
  },
}

// ==================== CRM 管理 API ====================

export const crmApi = {
  // 查看所有冲突项目
  getConflicts: async (params?: {
    status?: string
    twitter_handle?: string
    bb_owner?: string
    page?: number
    limit?: number
  }) => {
    const { data } = await api.get('/v1/admin/conflicts', { params })
    return data
  },

  // 标记冲突已解决
  resolveConflict: async (projectId: string, notes?: string) => {
    const { data } = await api.post(`/v1/admin/conflicts/${projectId}/resolve`, {
      resolution_notes: notes,
    })
    return data
  },

  // 转移冲突项目归属给 CRM 指派 BD（管理员，真正改 owner）
  transferConflicts: async (
    projectIds: string[],
    opts?: { target_bd_email?: string; resolution_notes?: string },
  ) => {
    const { data } = await api.post('/v1/admin/conflicts/transfer', {
      project_ids: projectIds,
      ...opts,
    })
    return data
  },

  // 手动触发扫描
  triggerScan: async () => {
    const { data } = await api.post('/v1/admin/scan/trigger')
    return data
  },

  // 查看扫描报告
  getScanReports: async (params?: { page?: number; limit?: number }) => {
    const { data } = await api.get('/v1/admin/scan/reports', { params })
    return data
  },

  // Admin 查看所有账号（含所属 BD）
  adminListAccounts: async (params?: {
    account_type?: string
    bd_user_id?: string
    page?: number
    limit?: number
  }) => {
    const { data } = await api.get('/v1/admin/accounts', { params })
    return data
  },

  // Admin 强制重绑账号
  forceRebindAccount: async (accountId: string, newUserId: string, reason: string) => {
    const { data } = await api.post(`/v1/admin/accounts/${accountId}/rebind`, {
      new_user_id: newUserId,
      reason,
    })
    return data
  },

  // 审计日志
  getAuditLogs: async (params?: {
    action?: string
    resource_type?: string
    resource_id?: string
    user_id?: string
    page?: number
    limit?: number
  }) => {
    const { data } = await api.get('/v1/admin/audit-logs', { params })
    return data
  },

  // 公海池
  getPublicPool: async (params?: { page?: number; limit?: number }) => {
    const { data } = await api.get('/v1/admin/public-pool', { params })
    return data
  },

  claimPublicPoolProject: async (projectId: string) => {
    const { data } = await api.post(`/v1/admin/public-pool/${projectId}/claim`)
    return data
  },
}

// ==================== 触达相关 API ====================

export const outreachApi = {
  // 触达中心项目列表（按渠道+完整性+发送状态筛选）
  getProjects: async (params: {
    channel: string
    send_status?: string  // unsent / sent / failed
    info_complete?: string  // complete / incomplete / all
    search?: string
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<Project>> => {
    const { data } = await api.get('/outreach/projects', { params })
    return data
  },

  // 获取触达记录列表
  getList: async (params?: {
    project_id?: string
    channel?: string
    status?: string
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<OutreachRecord>> => {
    const { data } = await api.get('/outreach', { params })
    return data
  },

  // 获取单条触达记录
  getById: async (id: string): Promise<OutreachRecord> => {
    const { data } = await api.get(`/outreach/${id}`)
    return data
  },

  // 创建触达任务
  createTask: async (task: OutreachTask): Promise<BatchResult> => {
    const { data } = await api.post('/outreach/task', task)
    return data
  },

  // 预览触达内容
  preview: async (params: {
    project_id: string
    channel: string
    phase: number
    template?: string
  }): Promise<PreviewContent> => {
    const { data } = await api.post('/outreach/preview', params)
    return data
  },

  // 发送单条触达（立即发送并更新状态）
  send: async (params: {
    record_id: string
    project_id?: string
    channel?: string
    phase?: number
    content?: string
  }): Promise<{
    success: boolean
    record_id: string
    status: string
    sender: string
    recipient: string
    channel: string
    project_name: string
    error?: string
    sent_at?: string
    tracking_id?: string
  }> => {
    // Use longer timeout for send operation (Telegram DM can take time to fetch admins and activity)
    const { data } = await api.post('/outreach/send', params, { timeout: 320000 })
    return data
  },

  // 重试失败的触达
  retry: async (id: string): Promise<OutreachRecord> => {
    const { data } = await api.post(`/outreach/${id}/retry`)
    return data
  },

  // 取消待发送的触达
  cancel: async (id: string): Promise<void> => {
    await api.post(`/outreach/${id}/cancel`)
  },

  // 获取队列状态
  getQueueStatus: async (): Promise<{
    email: { pending: number; processing: number }
    twitter: { pending: number; processing: number }
    telegram: { pending: number; processing: number }
  }> => {
    const { data } = await api.get('/outreach/queue')
    return data
  },

  // 暂停队列
  pauseQueue: async (channel?: string): Promise<void> => {
    await api.post('/outreach/queue/pause', { channel })
  },

  // 恢复队列
  resumeQueue: async (channel?: string): Promise<void> => {
    await api.post('/outreach/queue/resume', { channel })
  },

  // 批量立即发送
  batchSend: async (recordIds: string[]): Promise<{ sent: number; failed: number; rate_limited: number; total: number; errors?: string[] }> => {
    const { data } = await api.post('/outreach/batch-send', { record_ids: recordIds })
    return data
  },

  // P0-1: 一步式触达 — 创建并立即发送
  createAndSend: async (params: {
    project_ids: string[]
    channel: string
  }): Promise<{
    sent: number; failed: number; skipped: number; total: number
    results: Array<{ project_id: string; project_name: string; status: string; error?: string }>
  }> => {
    const { data } = await api.post('/outreach/create-and-send', params, { timeout: 320000 })
    return data
  },

  // P1-3: 一键重试所有失败
  retryAllFailed: async (channel?: string): Promise<{ retried: number; message: string }> => {
    const { data } = await api.post('/outreach/retry-all-failed', { channel })
    return data
  },

  // P1-2: 项目触达时间线
  getProjectTimeline: async (projectId: string): Promise<{
    project: { id: string; name: string; status: string; email?: string; twitter_handle?: string; telegram_group?: string }
    timeline: Array<{
      type: 'outreach' | 'reply'; id: string; channel: string; status?: string
      content_preview?: string; subject?: string; sender?: string; intent?: string
      timestamp?: string; sent_at?: string; opened_at?: string; error_message?: string
    }>
    summary: { total_outreach: number; total_replies: number; channels_used: string[]; has_reply: boolean }
  }> => {
    const { data } = await api.get(`/outreach/project-timeline/${projectId}`)
    return data
  },

  // P1-1: 策略预设列表
  getStrategyPresets: async (): Promise<{
    presets: Array<{
      key: string; name: string; description: string
      channels: string[]; intervals: Record<string, number>; icon: string
    }>
  }> => {
    const { data } = await api.get('/outreach/strategy-presets')
    return data
  },

  // P1-1: 应用策略到项目
  applyStrategy: async (params: {
    project_ids: string[]
    strategy?: string
    channels?: string[]
    intervals?: Record<string, number>
  }): Promise<{
    created: number; skipped: number; total_projects: number
    channels: string[]; errors?: string[]
  }> => {
    const { data } = await api.post('/outreach/apply-strategy', params)
    return data
  },
}

// ==================== 项目看板 API ====================

export const kanbanApi = {
  // P2-1: 获取看板数据
  getColumns: async (): Promise<{
    columns: Array<{
      key: string; title: string; count: number
      projects: Array<{
        id: string; name: string; website?: string; priority_score?: number
        email?: string; twitter_handle?: string; telegram_group?: string
        last_outreach_at?: string; next_outreach_at?: string
      }>
    }>
  }> => {
    const { data } = await api.get('/projects/kanban')
    return data
  },
}

// ==================== 回复相关 API ====================

export const replyApi = {
  // 获取回复列表
  getList: async (params?: {
    project_id?: string
    channel?: string
    intent?: string
    is_processed?: boolean
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<Reply>> => {
    const { data } = await api.get('/replies', { params })
    return data
  },

  // 获取单条回复
  getById: async (id: string): Promise<Reply> => {
    const { data } = await api.get(`/replies/${id}`)
    return data
  },

  // 标记回复为已处理
  markProcessed: async (id: string, params?: {
    intent?: string
    notes?: string
  }): Promise<Reply> => {
    const { data } = await api.post(`/replies/${id}/process`, params)
    return data
  },

  // 批量标记已处理
  batchProcess: async (ids: string[], intent?: string): Promise<BatchResult> => {
    const { data } = await api.post('/replies/batch-process', { ids, intent })
    return data
  },

  // 重新分类意图
  reclassify: async (id: string): Promise<Reply> => {
    const { data } = await api.post(`/replies/${id}/reclassify`)
    return data
  },

  // 获取未处理回复数量
  getUnprocessedCount: async (): Promise<{ total: number; by_intent: Record<string, number> }> => {
    const { data } = await api.get('/replies/unprocessed-count')
    return data
  },
}

// ==================== 账号相关 API ====================

export const accountApi = {
  // 获取系统预设的默认值（BD 创建账户时自动填充）
  getSystemDefaults: async (): Promise<{
    email: { account_name: string; display_name: string; from_email: string; company: string; smtp_host: string; smtp_port: number; smtp_configured: boolean; credentials_hidden: boolean }
    twitter: { account_name: string; configured: boolean; credentials_hidden: boolean; api_key_preview?: string }
    telegram: { account_name: string; needs_qr_auth: boolean; api_configured: boolean; credentials_hidden: boolean }
    user_info: { display_name: string; email: string; is_admin: boolean }
    system_limits: { daily_email: number; daily_twitter: number; daily_telegram: number; hourly_email: number; hourly_telegram: number }
  }> => {
    const { data } = await api.get('/accounts/system-defaults')
    return data
  },

  // BD 视角的账户摘要（只有状态和用量）
  getSummary: async (): Promise<{
    email: { account_name: string; health: string; daily_usage: number; daily_limit: number; total_sent: number; last_used?: string; restrict_until?: string }
    twitter: { account_name: string; health: string; daily_usage: number; daily_limit: number; total_sent: number; last_used?: string }
    telegram: { account_name: string; health: string; daily_usage: number; daily_limit: number; total_sent: number; last_used?: string }
    system_configured: { email: boolean; twitter: boolean; telegram_api: boolean }
  }> => {
    const { data } = await api.get('/accounts/summary')
    return data
  },

  // 获取账号列表
  getList: async (): Promise<AccountState[]> => {
    const { data } = await api.get('/accounts')
    return data
  },

  // 获取单个账号
  getById: async (id: string): Promise<AccountState> => {
    const { data } = await api.get(`/accounts/${id}`)
    return data
  },

  // 创建账号
  create: async (account: Partial<AccountState>): Promise<AccountState> => {
    const { data } = await api.post('/accounts', account)
    return data
  },

  // 更新账号
  update: async (id: string, account: Partial<AccountState>): Promise<AccountState> => {
    const { data } = await api.put(`/accounts/${id}`, account)
    return data
  },

  // 删除账号
  delete: async (id: string): Promise<void> => {
    await api.delete(`/accounts/${id}`)
  },

  // 重置每日使用量
  resetDailyUsage: async (id?: string): Promise<void> => {
    await api.post('/accounts/reset-daily', { id })
  },

  // 测试账号连接 (已存在的账户)
  testConnection: async (idOrConfig: string | { type: string; config: Record<string, unknown> }): Promise<{ success: boolean; message: string }> => {
    if (typeof idOrConfig === 'string') {
      const { data } = await api.post(`/accounts/${idOrConfig}/test`)
      return data
    } else {
      // 测试新配置 (不需要账户ID)
      const { data } = await api.post('/accounts/test', idOrConfig)
      return data
    }
  },

  // 解除限制
  unrestrict: async (id: string): Promise<AccountState> => {
    const { data } = await api.post(`/accounts/${id}/unrestrict`)
    return data
  },

  // 获取已保存的 API 凭据 (解密)
  getCredentials: async (id: string): Promise<{ api_id: string; api_hash: string }> => {
    const { data } = await api.get(`/accounts/${id}/credentials`)
    return data
  },

  // 保存 API 凭据 (加密存储)
  saveCredentials: async (id: string, apiId: string, apiHash: string): Promise<void> => {
    await api.post(`/accounts/${id}/credentials`, { api_id: apiId, api_hash: apiHash })
  },

  // 删除已保存的 API 凭据
  deleteCredentials: async (id: string): Promise<void> => {
    await api.delete(`/accounts/${id}/credentials`)
  },

  // Email/Telegram 触达队列状态（BD 独立）
  getOutreachQueueStatus: async (): Promise<{
    total_queued: number; total_sending: number
    channels: Record<string, { queued: number; sending: number; sent_today: number; failed: number }>
  }> => {
    const { data } = await api.get('/accounts/outreach-queue-status')
    return data
  },

  // Twitter DM 全局队列状态
  getTwitterQueueStatus: async (): Promise<{
    queued: number; sending: number; total_sent_today: number
    daily_quota: number; remaining: number
    rate_limited: boolean; rate_limit_remaining_seconds: number
    accounts: string[]; my_queued: number; my_sent_today: number
    by_user: Record<string, number>
  }> => {
    const { data } = await api.get('/accounts/twitter/queue-status')
    return data
  },

  // 检查 Twitter API 状态
  getTwitterApiStatus: async (): Promise<{
    configured: boolean
    authenticated: boolean
    rate_limited: boolean
    rate_limit_remaining_seconds: number
    error: string | null
  }> => {
    const { data } = await api.get('/accounts/twitter/api-status')
    return data
  },
}

// ==================== 统计相关 API ====================

export const statsApi = {
  // 获取仪表盘统计
  getDashboard: async (): Promise<DashboardStats> => {
    const { data } = await api.get('/stats/dashboard')
    return data
  },

  // 获取每日统计
  getDaily: async (params?: {
    start_date?: string
    end_date?: string
  }): Promise<DailyStats[]> => {
    const { data } = await api.get('/stats/daily', { params })
    return data
  },

  // 获取渠道统计
  getByChannel: async (params?: {
    start_date?: string
    end_date?: string
  }): Promise<Record<string, {
    sent: number
    delivered: number
    opened: number
    clicked: number
    replied: number
  }>> => {
    const { data } = await api.get('/stats/by-channel', { params })
    return data
  },

  // 获取转化漏斗
  getFunnel: async (): Promise<{
    stages: Array<{
      name: string
      count: number
      rate: number
    }>
  }> => {
    const { data } = await api.get('/stats/funnel')
    return data
  },

  // 获取趋势数据
  getTrends: async (params: {
    metric: string
    period: 'day' | 'week' | 'month'
    days?: number
  }): Promise<Array<{
    date: string
    value: number
  }>> => {
    const { data } = await api.get('/stats/trends', { params })
    return data
  },
}

// ==================== 配置相关 API ====================

export const configApi = {
  // 获取系统配置
  get: async (): Promise<SystemConfig> => {
    const { data } = await api.get('/config')
    return data
  },

  // 更新系统配置
  update: async (config: Partial<SystemConfig>): Promise<SystemConfig> => {
    const { data } = await api.put('/config', config)
    return data
  },

  // 获取模板列表（可选channel参数）
  getTemplates: async (channel?: string): Promise<Record<string, string> | { items: any[] }> => {
    if (channel) {
      const { data } = await api.get(`/config/templates/${channel}`)
      return data
    } else {
      const { data } = await api.get('/config/templates')
      return data
    }
  },

  // 更新模板
  updateTemplate: async (channel: string, name: string, content: string): Promise<void> => {
    await api.put(`/config/templates/${channel}/${name}`, { content })
  },

  // 测试webhook
  testWebhook: async (type: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post('/config/test-webhook', { type })
    return data
  },
}

// ==================== 用户配置 API ====================

export interface UserConfig {
  id: string
  user_id: string
  execution_mode: 'auto' | 'manual'
  email_to_twitter_minutes: number
  twitter_to_telegram_minutes: number
  phase_interval_minutes: number
  send_window_start: string
  send_window_end: string
  send_window_timezone: string
  skip_weekends: boolean
  llm_enabled: boolean
  llm_provider: 'openai' | 'anthropic'
  llm_api_key: string | null
  llm_model: string
  templates: Record<string, Record<string, unknown>>
  daily_email_limit: number
  daily_twitter_limit: number
  daily_telegram_limit: number
  created_at: string
  updated_at: string
}

export interface UserConfigUpdate {
  execution_mode?: 'auto' | 'manual'
  email_to_twitter_minutes?: number
  twitter_to_telegram_minutes?: number
  phase_interval_minutes?: number
  send_window_start?: string
  send_window_end?: string
  send_window_timezone?: string
  skip_weekends?: boolean
  llm_enabled?: boolean
  llm_provider?: 'openai' | 'anthropic'
  llm_api_key?: string
  llm_model?: string
  email_app_password?: string  // Google Workspace 应用专属密码
  telegram_admin_dm_percent?: number  // 对群管理员 DM 的百分比（默认 60%）
  templates?: Record<string, Record<string, unknown>>
  daily_email_limit?: number
  daily_twitter_limit?: number
  daily_telegram_limit?: number
}

export const userConfigApi = {
  // 获取当前用户配置
  get: async (): Promise<UserConfig> => {
    const { data } = await api.get('/user/config')
    return data
  },

  // 更新用户配置
  update: async (config: UserConfigUpdate): Promise<UserConfig> => {
    const { data } = await api.put('/user/config', config)
    return data
  },

  // 更新用户话术模板
  updateTemplates: async (templates: Record<string, unknown>): Promise<{ message: string; templates: Record<string, unknown> }> => {
    const { data } = await api.put('/user/config/templates', templates)
    return data
  },

  // 更新指定渠道和阶段的模板
  updateChannelTemplate: async (
    channel: string,
    phase: string,
    templateData: Record<string, unknown>
  ): Promise<{ message: string; template: Record<string, unknown> }> => {
    const { data } = await api.put(`/user/config/templates/${channel}/${phase}`, templateData)
    return data
  },

  // 测试LLM连接
  testLlm: async (params: {
    api_key?: string
    provider?: string
    model?: string
  }): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post('/user/config/test-llm', params)
    return data
  },
}

// ==================== 系统相关 API ====================

export const systemApi = {
  // 健康检查
  health: async (): Promise<{ status: string; version: string }> => {
    const { data } = await api.get('/health')
    return data
  },

  // 获取操作日志
  getLogs: async (params?: {
    level?: string
    category?: string
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<{
    id: string
    level: string
    category: string
    message: string
    created_at: string
  }>> => {
    const { data } = await api.get('/logs', { params })
    return data
  },

  // 触发抓取任务
  triggerScrape: async (params?: {
    source?: string
    limit?: number
  }): Promise<{ task_id: string }> => {
    const { data } = await api.post('/scrape/trigger', params)
    return data
  },

  // 获取任务状态
  getTaskStatus: async (taskId: string): Promise<{
    status: string
    progress: number
    result?: unknown
    error?: string
  }> => {
    const { data } = await api.get(`/tasks/${taskId}`)
    return data
  },
}

// ==================== 抓取 API ====================

export const scrapeApi = {
  // 批量抓取
  batchScrape: async (params: {
    project_ids?: string[]
    scrape_types?: string[]
    force?: boolean
  }): Promise<{
    total: number
    success: number
    failed: number
    results: Array<{
      project_id: string
      project_name: string
      has_success: boolean
      has_failure: boolean
      email?: {
        status: string
        data?: string
        message: string
      }
      twitter?: {
        status: string
        data?: string
        message: string
      }
      telegram?: {
        status: string
        data?: string
        message: string
      }
    }>
  }> => {
    const { data } = await api.post('/scrape/batch', params)
    return data
  },

  // 查询抓取任务进度（轮询用）
  getTaskStatus: async (taskId: string): Promise<{
    task_id: string; status: string; total: number; completed: number
    success: number; failed: number; current_project: string | null
    progress_percent: number; results: any[]
  }> => {
    const { data } = await api.get(`/scrape/task/${taskId}`)
    return data
  },

  // 获取项目抓取状态
  getScrapeStatus: async (projectId: string): Promise<{
    project_id: string
    project_name: string
    email: {
      status: string
      scraped_at: string | null
      data: string | null
    }
    twitter: {
      status: string
      scraped_at: string | null
      data: string | null
    }
    telegram: {
      status: string
      scraped_at: string | null
      data: string | null
    }
  }> => {
    const { data } = await api.get(`/scrape/${projectId}/status`)
    return data
  },
}

export default api
